import { ExternalLink } from "lucide-react";
import { cx, formatDateTime, formatTime } from "@amber/ui";
import type { AttemptView } from "../../contracts/index";
import { RecordTable } from "./RecordTable";

function shortDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${String(s)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 === 0 ? `${String(m)} min` : `${String(m)} min ${String(s % 60)} s`;
  return `${String(Math.floor(m / 60))} h ${String(m % 60)} min`;
}

function finishedText(a: AttemptView): string {
  if (!a.finished_at) return "Not finished";
  const sameDay = a.finished_at.slice(0, 10) === a.started_at.slice(0, 10);
  return `to ${sameDay ? formatTime(a.finished_at) : formatDateTime(a.finished_at)}`;
}

function AttemptRow({ a }: { a: AttemptView }) {
  return (
    <tr>
      <td className="whitespace-nowrap">
        <span className="font-mono">
          {a.number} of {a.of}
        </span>
        <div className="text-md text-muted">
          {a.stage_name}
          {a.kind === "manual" ? " · manual" : ""}
        </div>
      </td>
      <td>{a.route ?? <span className="text-muted">—</span>}</td>
      <td className="whitespace-nowrap">
        {formatDateTime(a.started_at)}
        <div className="text-md text-muted">{finishedText(a)}</div>
      </td>
      <td className="font-mono whitespace-nowrap text-muted">
        {a.duration_ms === null ? "—" : shortDuration(a.duration_ms)}
      </td>
      <td className="max-w-[260px]">
        <span className={cx(a.state === "failed" && "text-attention")}>{a.result}</span>
        {a.detail ? <div className="text-md text-muted">{a.detail}</div> : null}
      </td>
      <td className="text-md whitespace-nowrap text-muted">
        {a.cost ? `$${a.cost.amount_decimal}${a.cost_estimated ? " est." : ""}` : "—"}
      </td>
      <td className="max-w-[240px] text-md">
        {a.next.text}
        {a.next.at ? ` at ${formatDateTime(a.next.at)}` : ""}
      </td>
      <td className="text-md whitespace-nowrap">
        {a.engine_href ? (
          <a
            href={a.engine_href}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-link"
          >
            Engine run <ExternalLink size={11} aria-hidden="true" />
          </a>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
    </tr>
  );
}

// DI-76: each acquisition attempt with its evidence; DI-107: the engine run is the correlation reference.
export function AttemptsTable({ attempts }: { attempts: readonly AttemptView[] }) {
  return (
    <>
      <h3 className="m-0 mb-1.5 px-4 text-ui font-semibold max-compact:px-2">Acquisition attempts</h3>
      {attempts.length === 0 ? (
        <p className="m-0 px-4 pb-4 text-md text-muted max-compact:px-2">No acquisition attempts recorded.</p>
      ) : (
        <RecordTable
          label="Acquisition attempts"
          head={["Attempt", "Route", "Started", "Duration", "Result", "Cost", "Next", "Engine run"]}
        >
          {attempts.map((a) => (
            <AttemptRow key={a.id} a={a} />
          ))}
        </RecordTable>
      )}
    </>
  );
}
